import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService, TranslationService, UserIdService } from '@spartacus/core';
import { catchError, map, Observable, switchMap } from 'rxjs';
import { failStatus, StatusRequest, successStatus } from '../../../../core/models/request-status.model';
import { FrequencyRequest } from './quotes.model';
import { QuoteDetailsRequest } from '../../../../core/models/quotes.model';
import { ExcelDownload } from '../../../../core/models/excelDownload.model';

@Injectable({
  providedIn: 'root',
})
export class QuotesAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndPointService: OccEndpointsService,
    private userIdService: UserIdService,
  ) {}

  postQuote(quote: any): Observable<StatusRequest> {
    const url = this.occEndPointService.buildUrl('quotes');

    return this.http.post(url, quote).pipe(
      switchMap(() => {
        return this.translationService.translate('quotes.postSuccess').pipe(
          map(textSuccess => ({
            ...successStatus,
            message: textSuccess,
          })),
        );
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  getFrequency(): Observable<FrequencyRequest> {
    const url = this.occEndPointService.buildUrl('frequency');

    return this.http.get(url).pipe(
      map((response: any) => {
        return {
          frequencies: (response?.orderFrequencies || []).map((item: any) => ({
            code: item.code,
            label: item.name,
          })),
          ...successStatus,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  getUserQuoteHistory(from: string, to: string): Observable<any> {
    const url = this.occEndPointService.buildUrl('quoteHistory', {
      urlParams: {
        from,
        to,
      },
    });

    return this.http.get(url);
  }

  getUserQuoteDetails(uid: string): Observable<QuoteDetailsRequest> {
    const url = this.occEndPointService.buildUrl('quoteDetails', {
      urlParams: {
        uid,
      },
    });

    return this.http.get(url).pipe(
      map((response: any) => {
        return {
          ...{
            quote: response,
          },
          ...successStatus,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  addToCartQuoteLine(quote: string, code: string, quantity: number): Observable<any> {
    const url = this.occEndPointService.buildUrl('quoteAddToCart', {
      urlParams: {
        quote,
        code,
        quantity,
      },
    });

    return this.http.post(url, {}).pipe(
      switchMap(() => {
        return this.translationService.translate('quotes.addToCartSuccess', { code }).pipe(
          map(textSuccess => ({
            ...successStatus,
            message: textSuccess,
          })),
        );
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  exportToCSV(fromDate: string, toDate: string): Observable<{ obj?: ExcelDownload } & StatusRequest> {
    return this.userIdService.getUserId().pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('exportToCSV', { urlParams: { userId, fromDate, toDate } });
        return this.http.get(url).pipe(
          map((response: any) => {
            return {
              obj: response,
              ...successStatus,
            };
          }),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }
}
